import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import { useSnackbar } from 'notistack'
import { EstadoDeLista, EstadoDeListaTipo } from '@/components/common/EstadoDeLista'
import { tokens } from '@/theme'
import { campanhasService } from '@/services/campanhasService'
import { PermissoesGestaoComercial } from '@/utils/gestaoComercial'
import { CANAL_LABEL, CanalComercial } from '@/types/statusReserva'
import {
  AUDIENCIA_ROTULO,
  AUTOR_PAPEL_ROTULO,
  AlteracaoGestao,
  Campanha,
  CampanhaParticipante,
  CampanhaProducao,
  ConsultorElegivel,
  ORIGEM_ROTULO,
  PERIODICIDADE_ROTULO,
  audienciaDinamica,
  mensagemErroCampanha,
  rotuloAlteracao,
} from '@/types/campanha'

const formatBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

const formatDate = (iso?: string | null) => {
  if (!iso) return '—'
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

const formatDataHora = (iso?: string | null) => {
  if (!iso) return '—'
  const hora = iso.length > 15 ? ` ${iso.slice(11, 16)}` : ''
  return `${formatDate(iso)}${hora}`
}

const estadoDoErro = (e: unknown): EstadoDeListaTipo =>
  (e as { status?: number })?.status === 403 ? 'semPermissao' : 'erro'

const rotuloCanal = (canal?: string | null) =>
  canal ? (CANAL_LABEL[canal as CanalComercial] ?? canal) : 'Sem canal'

/** UUID cru na tabela não diz nada a ninguém: mostra o começo do id quando o nome não veio. */
const abreviar = (id: string) => (id.length > 8 ? `${id.slice(0, 8)}…` : id)

type Aba = 'regra' | 'participantes' | 'ranking' | 'historico'

/** Teto de `/consultores-elegiveis` no backend (`LIMITE_ELEGIVEIS`): a lista não é paginada. */
const LIMITE_ELEGIVEIS = 200

interface Props {
  campanha: Campanha | null
  /** Persona (AN-8 §4) — a tela esconde, o backend decide (403). */
  permissoes: PermissoesGestaoComercial
  onClose: () => void
  /** Disparado após inscrever/remover — a lista recarrega participantes e atingimento. */
  onAlterou?: () => void
}

/** Texto de "Quem participa" a partir da audiência gravada (AN-8 §3) — sem contar ninguém. */
const quemParticipa = (c: Campanha): string => {
  switch (c.audienceType) {
    case 'TODOS_ATIVOS':
      return 'Todos os consultores ativos da plataforma (inclusão automática de quem entrar depois)'
    case 'CANAIS':
      return `Consultores ativos dos canais: ${(c.audienciaCanais ?? []).map(rotuloCanal).join(', ') || '—'}`
    case 'EMPRESAS':
      return `Consultores ativos de ${(c.audienciaEmpresas ?? []).length} empresa(s) escolhida(s)`
    case 'NOMINAL':
      return 'Só os consultores inscritos na lista nominal; o canal é carimbado na inscrição'
    default:
      return 'Audiência não informada'
  }
}

/**
 * Detalhe da campanha (Figma 7.5 / A14; AN-8): Regra (só leitura, todos do back-office) ·
 * Participantes · Ranking por canal · Histórico — estas três só para quem gere participantes
 * (`GESTAO_CAMPANHA_EQUIPE`: ADMIN, ou GERENTE com GESTOR_EQUIPE, este só a própria equipe).
 * Os candidatos vêm de `/consultores-elegiveis` (nunca de `/v1/usuarios`).
 */
export const CampanhaDetalheDrawer: FC<Props> = ({ campanha, permissoes, onClose, onAlterou }) => {
  const { enqueueSnackbar } = useSnackbar()
  const campanhaId = campanha?.id
  const gere = permissoes.geriParticipantes
  const [aba, setAba] = useState<Aba>('regra')

  const [participantes, setParticipantes] = useState<CampanhaParticipante[]>([])
  const [producao, setProducao] = useState<CampanhaProducao | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [estadoErro, setEstadoErro] = useState<EstadoDeListaTipo | null>(null)

  const [elegiveis, setElegiveis] = useState<ConsultorElegivel[]>([])
  const [opcoes, setOpcoes] = useState<ConsultorElegivel[]>([])
  const [busca, setBusca] = useState('')
  const [selecionado, setSelecionado] = useState<ConsultorElegivel | null>(null)
  const [inscrevendo, setInscrevendo] = useState(false)
  const [removendo, setRemovendo] = useState<string | null>(null)
  /** Rascunho da meta individual por consultor (string do campo) enquanto o gestor digita. */
  const [metasEditando, setMetasEditando] = useState<Record<string, string>>({})

  const [historico, setHistorico] = useState<AlteracaoGestao[] | null>(null)
  const [estadoHistorico, setEstadoHistorico] = useState<EstadoDeListaTipo | null>(null)

  const dinamica = audienciaDinamica(campanha?.audienceType)
  const encerrada = campanha?.status === 'ENCERRADA'
  /** Inscrever à mão: só NOMINAL e fora de ENCERRADA (senão o backend recusa com 422). */
  const inscreveManual = gere && !dinamica && !encerrada
  /** Remover só em RASCUNHO: ATIVA só amplia e ENCERRADA não muda (422 CAMPANHA_AUDIENCIA_IMUTAVEL). */
  const removeManual = inscreveManual && campanha?.status === 'RASCUNHO'
  const elegiveisNoLimite = elegiveis.length >= LIMITE_ELEGIVEIS

  const nomes = useMemo(() => {
    const mapa = new Map<string, string>()
    participantes.forEach((p) => p.nome && mapa.set(p.consultorId, p.nome))
    producao?.ranking.forEach((r) => r.nome && mapa.set(r.consultorId, r.nome))
    return mapa
  }, [participantes, producao])

  const rotulo = (consultorId: string, nome?: string | null) =>
    nome || nomes.get(consultorId) || abreviar(consultorId)

  const carregarElegiveis = useCallback(async () => {
    if (!campanhaId) return
    try {
      const lista = await campanhasService.consultoresElegiveis(campanhaId)
      setElegiveis(lista)
      setOpcoes(lista)
    } catch {
      setElegiveis([])
      setOpcoes([])
    }
  }, [campanhaId])

  const carregar = useCallback(async () => {
    if (!campanhaId || !gere) return
    setCarregando(true)
    setEstadoErro(null)
    try {
      const [lista, prod] = await Promise.all([
        campanhasService.listarParticipantes(campanhaId),
        campanhasService.producao(campanhaId),
      ])
      setParticipantes(lista)
      setProducao(prod)
    } catch (e) {
      setEstadoErro(estadoDoErro(e))
    } finally {
      setCarregando(false)
    }
  }, [campanhaId, gere])

  // Reage ao id, não ao objeto: a lista da página recarrega a campanha após inscrever/remover
  // e o drawer só precisa dos números novos, sem voltar para a primeira aba.
  useEffect(() => {
    if (!campanhaId) return
    setAba('regra')
    setSelecionado(null)
    setBusca('')
    setHistorico(null)
    setEstadoHistorico(null)
    setParticipantes([])
    setProducao(null)
    setElegiveis([])
    setOpcoes([])
    void carregar()
  }, [campanhaId, carregar])

  // Elegíveis só quando a inscrição manual é possível (NOMINAL, não encerrada).
  useEffect(() => {
    if (inscreveManual) void carregarElegiveis()
  }, [inscreveManual, carregarElegiveis])

  // Busca no servidor (o backend limita a 200): sem texto, a lista base já carregada.
  useEffect(() => {
    if (!campanhaId || !inscreveManual) return
    const termo = busca.trim()
    if (!termo) {
      setOpcoes(elegiveis)
      return
    }
    const t = setTimeout(() => {
      campanhasService
        .consultoresElegiveis(campanhaId, termo)
        .then(setOpcoes)
        .catch(() => setOpcoes([]))
    }, 300)
    return () => clearTimeout(t)
  }, [busca, campanhaId, inscreveManual, elegiveis])

  const carregarHistorico = useCallback(async () => {
    if (!campanhaId) return
    setEstadoHistorico('carregando')
    try {
      const linhas = await campanhasService.alteracoes(campanhaId)
      setHistorico(linhas)
      setEstadoHistorico(null)
    } catch (e) {
      setEstadoHistorico(estadoDoErro(e))
    }
  }, [campanhaId])

  useEffect(() => {
    if (aba === 'historico' && historico === null && estadoHistorico === null) void carregarHistorico()
  }, [aba, historico, estadoHistorico, carregarHistorico])

  const depoisDeAlterar = async () => {
    setHistorico(null)
    await Promise.all([carregar(), carregarElegiveis()])
    onAlterou?.()
  }

  const inscrever = async () => {
    if (!campanha || !selecionado) return
    setInscrevendo(true)
    try {
      // Tenant e canal são carimbados pelo backend a partir da projeção.
      await campanhasService.inscrever(campanha.id, { consultorId: selecionado.consultorId })
      enqueueSnackbar('Participante inscrito', { variant: 'success' })
      setSelecionado(null)
      setBusca('')
      await depoisDeAlterar()
    } catch (e) {
      const status = (e as { status?: number })?.status
      enqueueSnackbar(
        status === 409
          ? 'Este consultor já participa da campanha'
          : mensagemErroCampanha(e, 'Não foi possível inscrever o participante'),
        { variant: status === 409 ? 'warning' : 'error' }
      )
    } finally {
      setInscrevendo(false)
    }
  }

  /**
   * ADMIN: "Inscrever todos os elegíveis"; GERENTE+GESTOR: "Inscrever minha equipe" — o
   * backend já devolve só a equipe dele como elegível. Lote tudo ou nada.
   */
  const inscreverLote = async () => {
    if (!campanha || elegiveis.length === 0) return
    setInscrevendo(true)
    try {
      const criados = await campanhasService.inscreverLote(
        campanha.id,
        elegiveis.map((u) => ({ consultorId: u.consultorId }))
      )
      enqueueSnackbar(`${criados.length} consultor(es) inscrito(s)`, { variant: 'success' })
      await depoisDeAlterar()
    } catch (e) {
      enqueueSnackbar(mensagemErroCampanha(e, 'Não foi possível inscrever os consultores'), {
        variant: 'error',
      })
    } finally {
      setInscrevendo(false)
    }
  }

  /** Grava a meta individual ao sair do campo; vazio volta à meta padrão da campanha. */
  const salvarMeta = async (p: CampanhaParticipante) => {
    if (!campanha) return
    const texto = metasEditando[p.consultorId]
    if (texto === undefined) return
    const valor = texto.trim() === '' ? null : Number(texto)
    if (valor === (p.valorMeta ?? null)) return
    if (valor !== null && (!Number.isFinite(valor) || valor <= 0)) {
      enqueueSnackbar('Meta individual deve ser positiva', { variant: 'warning' })
      return
    }
    try {
      await campanhasService.atualizarMetaParticipante(campanha.id, p.consultorId, valor)
      enqueueSnackbar('Meta individual atualizada', { variant: 'success' })
      setMetasEditando((m) => {
        const resto = { ...m }
        delete resto[p.consultorId]
        return resto
      })
      setHistorico(null)
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroCampanha(e, 'Não foi possível salvar a meta'), { variant: 'error' })
    }
  }

  const remover = async (p: CampanhaParticipante) => {
    if (!campanha) return
    setRemovendo(p.consultorId)
    try {
      await campanhasService.remover(campanha.id, p.consultorId)
      enqueueSnackbar('Participante removido', { variant: 'success' })
      await depoisDeAlterar()
    } catch (e) {
      enqueueSnackbar(mensagemErroCampanha(e, 'Não foi possível remover'), { variant: 'error' })
    } finally {
      setRemovendo(null)
    }
  }

  const abaRegra = () =>
    campanha && (
      <Stack spacing={1.5}>
        <Linha rotulo="Período" valor={`${formatDate(campanha.dataInicio)} → ${formatDate(campanha.dataFim)}`} />
        <Linha
          rotulo="Periodicidade"
          valor={`${PERIODICIDADE_ROTULO[campanha.periodicidade ?? 'PERIODO']}${
            campanha.rotuloPeriodo ? ` · período corrente: ${campanha.rotuloPeriodo}` : ''
          }`}
        />
        <Linha rotulo="Produto" valor={campanha.produtoNome ?? 'Todos'} />
        <Linha
          rotulo="Meta coletiva"
          valor={campanha.valorMeta != null ? formatBRL(campanha.valorMeta) : 'Sem meta coletiva'}
        />
        <Linha
          rotulo="Meta por consultor (padrão)"
          valor={campanha.valorMetaParticipante ? formatBRL(campanha.valorMetaParticipante) : 'Não definida'}
        />
        {campanha.premiacao && (
          <Linha rotulo="Premiação (informativa — não gera pagamento)" valor={campanha.premiacao} />
        )}
        <Linha rotulo="Conta para a meta" valor="Reservas com estágio Ganho, pela data de assinatura (ou da última atualização) dentro do período" />
        <Linha
          rotulo={`Quem participa${campanha.audienceType ? ` · ${AUDIENCIA_ROTULO[campanha.audienceType]}` : ''}`}
          valor={quemParticipa(campanha)}
        />
        {campanha.audienciaMaterializadaEm && (
          <Linha rotulo="Audiência atualizada em" valor={formatDataHora(campanha.audienciaMaterializadaEm)} />
        )}
        {campanha.descricao && <Linha rotulo="Descrição" valor={campanha.descricao} />}
        <Divider />
        <Box>
          <Typography variant="overline" sx={{ color: tokens.textSecondary }}>
            Atingimento
          </Typography>
          {campanha.atingimentoPercent != null && campanha.valorMeta != null ? (
            <>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, campanha.atingimentoPercent)}
                  sx={{ flex: 1, height: 8, borderRadius: 4 }}
                  color={campanha.atingimentoPercent >= 100 ? 'success' : 'primary'}
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {campanha.atingimentoPercent}%
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
                {formatBRL(campanha.producaoGanha ?? 0)} de {formatBRL(campanha.valorMeta)} ·{' '}
                {campanha.participantes ?? 0} participante(s)
              </Typography>
            </>
          ) : (
            <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
              Sem meta coletiva · {formatBRL(campanha.producaoGanha ?? 0)} produzidos ·{' '}
              {campanha.participantes ?? 0} participante(s)
            </Typography>
          )}
        </Box>
        {!gere && (
          <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
            Participantes, produção e histórico disponíveis para o administrador e para o gerente
            gestor de equipe
          </Alert>
        )}
      </Stack>
    )

  const abaParticipantes = () => (
    <Stack spacing={2}>
      {dinamica && (
        <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
          Audiência dinâmica ({AUDIENCIA_ROTULO[campanha!.audienceType!]}): a inscrição é automática.
          Para incluir ou tirar pessoas à mão, o administrador converte a campanha em lista nominal.
        </Alert>
      )}
      {!dinamica && encerrada && (
        <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
          Campanha encerrada: só leitura — a lista de participantes e as metas não mudam
        </Alert>
      )}
      {inscreveManual && !removeManual && (
        <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
          Campanha ativa: dá para inscrever mais consultores, mas não remover (a audiência só amplia)
        </Typography>
      )}
      {permissoes.ehGestorEquipe && (
        <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
          Você vê e gere só os consultores da sua equipe
        </Typography>
      )}
      {inscreveManual && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Autocomplete
            size="small"
            fullWidth
            options={opcoes}
            value={selecionado}
            inputValue={busca}
            onInputChange={(_, v, motivo) => motivo !== 'reset' && setBusca(v)}
            onChange={(_, v) => setSelecionado(v)}
            filterOptions={(x) => x}
            getOptionLabel={(u) => `${u.nome || u.email || abreviar(u.consultorId)}${u.canal ? ` · ${rotuloCanal(u.canal)}` : ''}`}
            isOptionEqualToValue={(a, b) => a.consultorId === b.consultorId}
            noOptionsText="Nenhum consultor elegível"
            renderInput={(params) => <TextField {...params} label="Consultor" placeholder="Buscar consultor" />}
          />
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            disabled={!selecionado || inscrevendo}
            onClick={() => void inscrever()}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Inscrever
          </Button>
        </Stack>
      )}
      {inscreveManual && (
        <Box>
          <Button
            size="small"
            variant="text"
            startIcon={<GroupAddIcon />}
            disabled={elegiveis.length === 0 || inscrevendo}
            onClick={() => void inscreverLote()}
          >
            {permissoes.ehGestorEquipe
              ? `Inscrever minha equipe (${elegiveis.length})`
              : `Inscrever os elegíveis listados (até ${LIMITE_ELEGIVEIS})`}
          </Button>
          {elegiveisNoLimite && (
            <Alert severity="warning" variant="outlined" sx={{ py: 0, mt: 1 }}>
              A lista de elegíveis veio com {LIMITE_ELEGIVEIS} consultores, o limite: pode haver outros. Depois de
              inscrever estes, use o botão de novo (ou a busca) para os restantes.
            </Alert>
          )}
        </Box>
      )}
      {participantes.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem="Nenhum participante inscrito"
          detalhe={
            inscreveManual
              ? 'Inscreva consultores para a campanha contar produção.'
              : dinamica
                ? 'A audiência é materializada pelo sistema ao ativar e na sincronização.'
                : undefined
          }
          minHeight={160}
        />
      ) : (
        <TableContainer>
          <Table size="small" aria-label="Participantes da campanha">
            <TableHead>
              <TableRow>
                <TableCell>Consultor</TableCell>
                <TableCell>Canal</TableCell>
                <TableCell>Origem</TableCell>
                <TableCell>Meta individual</TableCell>
                <TableCell>Inscrito em</TableCell>
                {removeManual && <TableCell align="right">Ações</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {participantes.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {rotulo(p.consultorId, p.nome)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={rotuloCanal(p.canal)} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{p.origem ? ORIGEM_ROTULO[p.origem] ?? p.origem : '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    {/* Meta individual: qualquer audiência (AN-8 §3); ENCERRADA é só leitura (422). */}
                    <TextField
                      size="small"
                      type="number"
                      disabled={encerrada}
                      value={metasEditando[p.consultorId] ?? (p.valorMeta != null ? String(p.valorMeta) : '')}
                      placeholder={
                        campanha?.valorMetaParticipante
                          ? `padrão: ${formatBRL(campanha.valorMetaParticipante)}`
                          : 'sem meta'
                      }
                      onChange={(e) => setMetasEditando((m) => ({ ...m, [p.consultorId]: e.target.value }))}
                      onBlur={() => void salvarMeta(p)}
                      inputProps={{ 'aria-label': `Meta individual de ${rotulo(p.consultorId, p.nome)}`, min: 0 }}
                    />
                  </TableCell>
                  <TableCell>{formatDate(p.inscritoEm)}</TableCell>
                  {removeManual && (
                    <TableCell align="right">
                      <Tooltip title="Remover da campanha">
                        <span>
                          <IconButton
                            size="small"
                            aria-label={`Remover ${rotulo(p.consultorId, p.nome)}`}
                            disabled={removendo === p.consultorId}
                            onClick={() => void remover(p)}
                          >
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  )

  const abaRanking = () => {
    if (!producao) return null
    const maiorCanal = producao.porCanal.reduce((m, c) => Math.max(m, c.producao), 0)
    const maiorParticipante = producao.ranking.reduce((m, r) => Math.max(m, r.producao), 0)
    return (
      <Stack spacing={3}>
        <Box>
          <Typography variant="overline" sx={{ color: tokens.textSecondary }}>
            Produção total
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: tokens.statusSuccess }}>
            {formatBRL(producao.producaoTotal)}
          </Typography>
          <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
            {producao.atingimentoPercent != null && producao.valorMeta != null
              ? `${producao.atingimentoPercent}% da meta de ${formatBRL(producao.valorMeta)}`
              : 'Sem meta coletiva'}
          </Typography>
        </Box>

        {producao.producaoEquipe != null && (
          <Box>
            <Typography variant="overline" sx={{ color: tokens.textSecondary }}>
              Sua equipe
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {formatBRL(producao.producaoEquipe)}
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
              {producao.participantesEquipe ?? 0} participante(s) da equipe · a posição é a da campanha inteira
            </Typography>
          </Box>
        )}

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Por canal
          </Typography>
          {producao.porCanal.length === 0 ? (
            <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
              Sem produção por canal ainda.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {producao.porCanal.map((c) => (
                <Box key={c.canal}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {c.canal}{' '}
                      <Typography component="span" variant="caption" sx={{ color: tokens.textSecondary }}>
                        · {c.participantes} participante(s) · {c.quantidade} reserva(s)
                      </Typography>
                    </Typography>
                    <Typography variant="body2">
                      {formatBRL(c.producao)} ({c.participacaoPercent}%)
                    </Typography>
                  </Stack>
                  <Box
                    role="img"
                    aria-label={`${c.canal}: ${c.participacaoPercent}% da produção`}
                    sx={{ height: 8, borderRadius: 4, backgroundColor: tokens.divider, overflow: 'hidden' }}
                  >
                    <Box
                      sx={{
                        width: `${maiorCanal > 0 ? (c.producao / maiorCanal) * 100 : 0}%`,
                        height: '100%',
                        backgroundColor: tokens.brandPrimary,
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Ranking
          </Typography>
          {producao.ranking.length === 0 ? (
            <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
              Nenhum participante inscrito.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small" aria-label="Ranking da campanha">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Consultor</TableCell>
                    <TableCell>Canal</TableCell>
                    <TableCell align="right">Produção</TableCell>
                    <TableCell align="right">% da meta coletiva</TableCell>
                    <TableCell align="right">Atingimento individual</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {producao.ranking.map((r) => (
                    <TableRow key={r.consultorId} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{r.posicao}º</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {rotulo(r.consultorId, r.nome)}
                        </Typography>
                        <Box
                          sx={{ height: 4, borderRadius: 2, backgroundColor: tokens.divider, mt: 0.5, overflow: 'hidden' }}
                        >
                          <Box
                            sx={{
                              width: `${maiorParticipante > 0 ? (r.producao / maiorParticipante) * 100 : 0}%`,
                              height: '100%',
                              backgroundColor: tokens.statusSuccess,
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>{r.canal ?? 'Sem canal'}</TableCell>
                      <TableCell align="right">
                        {formatBRL(r.producao)}
                        <Typography variant="caption" sx={{ display: 'block', color: tokens.textSecondary }}>
                          {r.quantidade} reserva(s)
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{r.atingimentoPercent != null ? `${r.atingimentoPercent}%` : '—'}</TableCell>
                      <TableCell align="right">
                        {r.atingimentoIndividualPercent != null ? (
                          <>
                            {r.atingimentoIndividualPercent}%
                            <Typography variant="caption" sx={{ display: 'block', color: tokens.textSecondary }}>
                              de {formatBRL(r.metaIndividual ?? 0)}
                            </Typography>
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Stack>
    )
  }

  const abaHistorico = () => {
    if (estadoHistorico) {
      return (
        <EstadoDeLista
          estado={estadoHistorico}
          minHeight={160}
          {...(estadoHistorico === 'erro'
            ? { acao: { label: 'Tentar novamente', onClick: () => void carregarHistorico() } }
            : {})}
        />
      )
    }
    if (!historico || historico.length === 0) {
      return <EstadoDeLista estado="vazio" mensagem="Nenhuma alteração registrada" minHeight={160} />
    }
    return (
      <TableContainer>
        <Table size="small" aria-label="Histórico da campanha">
          <TableHead>
            <TableRow>
              <TableCell>Quando</TableCell>
              <TableCell>Alteração</TableCell>
              <TableCell>De → Para</TableCell>
              <TableCell>Autor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {historico.map((h) => (
              <TableRow key={h.id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDataHora(h.criadoEm)}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {rotuloAlteracao(h.tipo)}
                  </Typography>
                  {h.consultorId && (
                    <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
                      {rotulo(h.consultorId)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {h.valorAnterior ?? '—'} → {h.valorNovo ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell>{AUTOR_PAPEL_ROTULO[h.autorPapel] ?? h.autorPapel}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  const conteudo = () => {
    if (aba === 'regra') return abaRegra()
    if (aba === 'historico') return abaHistorico()
    if (carregando && !producao) return <EstadoDeLista estado="carregando" minHeight={200} />
    if (estadoErro) {
      return (
        <EstadoDeLista
          estado={estadoErro}
          minHeight={200}
          {...(estadoErro === 'erro' ? { acao: { label: 'Tentar novamente', onClick: () => void carregar() } } : {})}
        />
      )
    }
    if (aba === 'participantes') return abaParticipantes()
    return abaRanking()
  }

  return (
    <Drawer
      anchor="right"
      open={!!campanha}
      onClose={onClose}
      PaperProps={{ role: 'dialog', 'aria-label': 'Detalhe da campanha', sx: { width: { xs: '100%', sm: 600 }, p: 3 } }}
    >
      {campanha && (
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="overline" sx={{ color: tokens.brandPrimary, fontWeight: 700 }}>
                Campanha
              </Typography>
              <Typography variant="h6">{campanha.nome}</Typography>
            </Box>
            <IconButton aria-label="Fechar" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Tabs value={aba} onChange={(_, v: Aba) => setAba(v)} variant="fullWidth">
            <Tab value="regra" label="Regra" />
            {gere && (
              <Tab value="participantes" label={`Participantes${producao ? ` (${producao.participantes})` : ''}`} />
            )}
            {gere && <Tab value="ranking" label="Ranking por canal" />}
            {gere && <Tab value="historico" label="Histórico" />}
          </Tabs>

          {conteudo()}
        </Stack>
      )}
    </Drawer>
  )
}

const Linha: FC<{ rotulo: string; valor: string }> = ({ rotulo, valor }) => (
  <Box>
    <Typography variant="overline" sx={{ color: tokens.textSecondary }}>
      {rotulo}
    </Typography>
    <Typography variant="body2">{valor}</Typography>
  </Box>
)

export default CampanhaDetalheDrawer
